import { useEffect, useRef, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Save, Plus, Edit, Trash2, Users, CalendarDays, Paperclip, Download, X, Upload, Phone, UserCheck, BadgeCheck, MessageCircle, Copy, RefreshCw, ChevronDown, FileSpreadsheet, FileType2, KeyRound, AlertCircle, Rocket, Sparkles, Activity, UserCog, Shield, ShieldCheck, Moon, Sun, Smartphone, MapPin, Crosshair } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import AuditLog from "./AuditLog";
import Sellers from "./Sellers";

const emptyEmployee = {
  nombre_completo: "",
  cedula: "",
  puesto: "",
  telefono: "",
  email: "",
  fecha_ingreso: "",
  salario: "",
  contacto_emergencia_nombre: "",
  contacto_emergencia_telefono: "",
  vacaciones: [],
  documentos: [],
  notas: "",
  es_vendedor: false,
  codigo_vendedor: "",
  color_vendedor: "#10B981",
};

const VENDOR_COLOR_PALETTE = [
  "#10B981", "#002FA7", "#F59E0B", "#EF4444",
  "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16",
];

/**
 * Square logo uploader. Uploads via POST /api/company/logo (multipart) and
 * receives a server-served URL like /api/company/logo/{user_id}. The cache
 * busting query param forces the <img> to refresh after a re-upload.
 */
function CompanyLogoField({ company, onLogoChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const logoUrl = company.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${company.logo_url}?v=${company.logo_storage_path ? company.logo_storage_path.length : Date.now()}`
    : "";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onLogoChange(data.logo_url);
      toast.success("Logo cargado. No olvides guardar la empresa.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!company.logo_url) return;
    try {
      await api.delete("/company/logo");
      onLogoChange("");
      toast.success("Logo eliminado");
    } catch {
      toast.error("No se pudo eliminar el logo");
    }
  };

  return (
    <div className="flex items-start gap-4 pb-2">
      <div
        className="h-24 w-24 shrink-0 rounded-sm bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden"
        data-testid="company-logo-preview"
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" />
        ) : (
          <Building2 className="h-8 w-8 text-slate-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">Logo de la empresa</p>
        <p className="text-[11px] text-slate-500 mb-2">
          Recomendado cuadrado (1:1), PNG con fondo transparente. Aparece en facturas, catálogo PDF y catálogo público.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFile}
          data-testid="company-logo-input"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="company-logo-upload-button"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            {uploading ? "Subiendo…" : company.logo_url ? "Cambiar logo" : "Subir logo"}
          </Button>
          {company.logo_url && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-sm text-rose-600 hover:bg-rose-50"
              onClick={handleRemove}
              data-testid="company-logo-remove-button"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Quitar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const emptyVacation = {
  fecha_inicio: "",
  fecha_fin: "",
  dias: "",
  tipo: "Vacaciones",
  notas: "",
};

export default function MyCompany() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState({
    nombre_registrado: "",
    cedula: "",
    cedula_tipo: "juridica",
    direccion_cedi: "",
    cedi_maps_link: "",
    cedi_lat: 0,
    cedi_lng: 0,
    telefono1: "",
    whatsapp: "",
    horario: "",
    actividad_economica_numero: "",
    hora_salida_default: "08:00",
  });
  const [employees, setEmployees] = useState([]);
  const [empDialogOpen, setEmpDialogOpen] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [empForm, setEmpForm] = useState(emptyEmployee);
  const [vacationForm, setVacationForm] = useState(emptyVacation);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deleteEmpConfirm, setDeleteEmpConfirm] = useState(null);
  const [importResult, setImportResult] = useState(null); // { sellers_created, sellers_skipped, imported }

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [c, e] = await Promise.all([api.get("/company"), api.get("/employees")]);
      // Merge with defaults so we don't drop fields we don't render here
      // (e.g. catalogo_publico, moneda) when saving.
      setCompany((prev) => ({
        ...prev,
        ...c.data,
        hora_salida_default: c.data.hora_salida_default || "08:00",
        cedula_tipo: c.data.cedula_tipo || "juridica",
      }));
      setEmployees(e.data);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/company", company);
      toast.success("Datos de la empresa guardados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const openNewEmployee = () => {
    setEditingEmp(null);
    setEmpForm(emptyEmployee);
    setVacationForm(emptyVacation);
    setEmpDialogOpen(true);
  };

  const openEditEmployee = (emp) => {
    setEditingEmp(emp);
    setEmpForm({
      nombre_completo: emp.nombre_completo,
      cedula: emp.cedula || "",
      puesto: emp.puesto || "",
      telefono: emp.telefono || "",
      email: emp.email || "",
      fecha_ingreso: emp.fecha_ingreso || "",
      salario: emp.salario?.toString() || "",
      contacto_emergencia_nombre: emp.contacto_emergencia_nombre || "",
      contacto_emergencia_telefono: emp.contacto_emergencia_telefono || "",
      vacaciones: emp.vacaciones || [],
      documentos: emp.documentos || [],
      notas: emp.notas || "",
      es_vendedor: !!emp.es_vendedor,
      codigo_vendedor: emp.codigo_vendedor || "",
      color_vendedor: emp.color_vendedor || "#10B981",
    });
    setVacationForm(emptyVacation);
    setEmpDialogOpen(true);
  };

  const addVacation = () => {
    if (!vacationForm.fecha_inicio || !vacationForm.fecha_fin) {
      toast.error("Fecha de inicio y fin son requeridas");
      return;
    }
    const dias = parseFloat(vacationForm.dias);
    if (!dias || dias <= 0) {
      toast.error("Ingresa el número de días");
      return;
    }
    setEmpForm({
      ...empForm,
      vacaciones: [
        ...empForm.vacaciones,
        {
          id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
          fecha_inicio: vacationForm.fecha_inicio,
          fecha_fin: vacationForm.fecha_fin,
          dias,
          tipo: vacationForm.tipo || "Vacaciones",
          notas: vacationForm.notas || "",
        },
      ],
    });
    setVacationForm(emptyVacation);
  };

  const removeVacation = (id) => {
    setEmpForm({ ...empForm, vacaciones: empForm.vacaciones.filter(v => v.id !== id) });
  };

  const uploadEmployeeDoc = async (file) => {
    if (!editingEmp) {
      toast.error("Guarda el colaborador primero para subir documentos");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 10MB)");
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("titulo", file.name);
      fd.append("file", file);
      const res = await api.post(`/employees/${editingEmp.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEmpForm(prev => ({ ...prev, documentos: [...prev.documentos, res.data] }));
      toast.success("Documento subido");
    } catch {
      toast.error("Error al subir documento");
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteEmployeeDoc = async (docId) => {
    if (!editingEmp) return;
    try {
      await api.delete(`/employees/${editingEmp.id}/documents/${docId}`);
      setEmpForm(prev => ({ ...prev, documentos: prev.documentos.filter(d => d.id !== docId) }));
      toast.success("Documento eliminado");
    } catch {
      toast.error("Error al eliminar documento");
    }
  };

  const downloadEmployeeDoc = (docId) => {
    if (!editingEmp) return;
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/employees/${editingEmp.id}/documents/${docId}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  const submitEmployee = async (e) => {
    e.preventDefault();
    try {
      const data = { ...empForm, salario: parseFloat(empForm.salario) || 0 };
      if (editingEmp) {
        await api.put(`/employees/${editingEmp.id}`, { ...data, id: editingEmp.id });
        toast.success("Colaborador actualizado");
      } else {
        await api.post("/employees", data);
        toast.success("Colaborador agregado");
      }
      setEmpDialogOpen(false);
      fetchAll();
    } catch {
      toast.error("Error al guardar colaborador");
    }
  };

  const confirmDeleteEmp = async () => {
    if (!deleteEmpConfirm) return;
    try {
      await api.delete(`/employees/${deleteEmpConfirm.id}`);
      toast.success("Colaborador eliminado");
      setDeleteEmpConfirm(null);
      fetchAll();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const downloadEmployeeTemplate = async (format = "excel") => {
    try {
      const token = localStorage.getItem("token");
      const url = format === "excel"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/employees/template/excel`
        : `${process.env.REACT_APP_BACKEND_URL}/api/employees/template`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.setAttribute("download", format === "excel" ? "plantilla_colaboradores.xlsx" : "plantilla_colaboradores.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Plantilla ${format === "excel" ? "Excel" : "CSV"} descargada`);
    } catch {
      toast.error("Error al descargar plantilla");
    }
  };

  const importEmployees = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/employees/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported = 0, errors = [], sellers_created = [], sellers_skipped = [] } = res.data || {};
      if (imported > 0) {
        const extra = [];
        if (sellers_created.length) extra.push(`${sellers_created.length} cuenta(s) de vendedor`);
        if (sellers_skipped.length) extra.push(`${sellers_skipped.length} email(s) ya existían`);
        if (errors.length) extra.push(`${errors.length} con errores`);
        toast.success(
          `Importados ${imported} colaborador${imported === 1 ? "" : "es"}` +
            (extra.length ? ` · ${extra.join(" · ")}` : "")
        );
      } else {
        toast.error(errors[0] || "No se importó ningún colaborador");
      }
      if (sellers_created.length || sellers_skipped.length) {
        setImportResult({ imported, sellers_created, sellers_skipped });
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar colaboradores");
    }
  };

  const copyCredentials = async (seller) => {
    try {
      const text = `Email: ${seller.email}\nContraseña: ${seller.password}\nNombre: ${seller.nombre_completo}`;
      await navigator.clipboard.writeText(text);
      toast.success(`Credenciales de ${seller.nombre_completo} copiadas`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const copyAllCredentials = async () => {
    if (!importResult?.sellers_created?.length) return;
    try {
      const lines = importResult.sellers_created.map(
        (s) => `${s.nombre_completo}\n  Email: ${s.email}\n  Contraseña: ${s.password}`
      );
      await navigator.clipboard.writeText(lines.join("\n\n"));
      toast.success(`${importResult.sellers_created.length} credenciales copiadas`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const downloadOnboardingTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/template/excel`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error("Failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_onboarding_minegocio.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla Onboarding descargada");
    } catch {
      toast.error("Error al descargar plantilla");
    }
  };

  const importOnboarding = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/onboarding/import-all", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const {
        employees_imported = 0,
        customers_imported = 0,
        sellers_created = [],
        sellers_skipped = [],
        unresolved_vendors = [],
        errors = [],
      } = res.data || {};

      const bits = [];
      if (employees_imported) bits.push(`${employees_imported} colaborador${employees_imported === 1 ? "" : "es"}`);
      if (customers_imported) bits.push(`${customers_imported} cliente${customers_imported === 1 ? "" : "s"}`);
      if (sellers_created.length) bits.push(`${sellers_created.length} cuenta${sellers_created.length === 1 ? "" : "s"} de vendedor`);
      if (bits.length) toast.success("Importados: " + bits.join(", "));
      else toast.error(errors[0] || "No se importó nada");

      if (sellers_created.length || sellers_skipped.length || unresolved_vendors.length || errors.length) {
        setImportResult({
          imported: employees_imported,
          employees_imported,
          customers_imported,
          sellers_created,
          sellers_skipped,
          unresolved_vendors,
          errors,
          isOnboarding: true,
        });
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en el onboarding");
    }
  };

  if (loading) return <div className="text-center py-12">Cargando...</div>;

  return (
    <div data-testid="my-company-page">
      <div className="mb-6">
        <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Mi Empresa
        </h2>
        <p className="text-slate-500 text-sm mt-1">Datos de tu empresa y equipo de trabajo</p>
      </div>

      {/* Datos generales */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-[rgb(0,47,167)]" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Datos Generales
          </h3>
        </div>
        <form onSubmit={saveCompany} className="space-y-4">
          <CompanyLogoField company={company} onLogoChange={(logo_url) => setCompany((p) => ({ ...p, logo_url }))} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1.5">Nombre Registrado *</label>
              <Input
                value={company.nombre_registrado}
                onChange={(e) => setCompany({ ...company, nombre_registrado: e.target.value })}
                required
                className="rounded-sm"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Tipo de Cédula</label>
              <Select value={company.cedula_tipo} onValueChange={(v) => setCompany({ ...company, cedula_tipo: v })}>
                <SelectTrigger className="rounded-sm" data-testid="cedula-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="juridica">Cédula Jurídica</SelectItem>
                  <SelectItem value="fisica">Cédula Física</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Número de Cédula</label>
              <Input
                value={company.cedula}
                onChange={(e) => setCompany({ ...company, cedula: e.target.value })}
                className="rounded-sm"
                placeholder="3-101-XXXXXX"
                data-testid="cedula-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Teléfono</label>
              <Input
                value={company.telefono1}
                onChange={(e) => setCompany({ ...company, telefono1: e.target.value })}
                className="rounded-sm"
                data-testid="company-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">WhatsApp</label>
              <Input
                value={company.whatsapp}
                onChange={(e) => setCompany({ ...company, whatsapp: e.target.value })}
                className="rounded-sm"
                data-testid="company-whatsapp-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Horario</label>
              <Input
                value={company.horario}
                onChange={(e) => setCompany({ ...company, horario: e.target.value })}
                className="rounded-sm"
                placeholder="Lun-Vie 8:00-17:00"
                data-testid="company-schedule-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">N° Actividad Económica</label>
              <Input
                value={company.actividad_economica_numero}
                onChange={(e) => setCompany({ ...company, actividad_economica_numero: e.target.value })}
                className="rounded-sm"
                placeholder="Ej: 471101"
                data-testid="company-activity-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Hora Salida Predeterminada
                <span className="block text-[10px] text-slate-500 font-normal">Se usa como valor por defecto al planificar rutas</span>
              </label>
              <Input
                type="time"
                value={company.hora_salida_default}
                onChange={(e) => setCompany({ ...company, hora_salida_default: e.target.value })}
                className="rounded-sm"
                data-testid="company-default-departure-input"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200">
            <h4 className="text-sm font-semibold mb-3 text-[rgb(0,47,167)]">Centro de Distribución (CEDI)</h4>
            <p className="text-xs text-slate-500 mb-3">
              Esta dirección se usará como punto de inicio y fin para las rutas planificadas.
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Dirección del CEDI *</label>
                <Textarea
                  value={company.direccion_cedi}
                  onChange={(e) => setCompany({ ...company, direccion_cedi: e.target.value })}
                  className="rounded-sm"
                  rows={2}
                  placeholder="Dirección completa del centro de distribución..."
                  data-testid="cedi-address-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Link de Google Maps del CEDI <span className="text-slate-400 font-normal">(opcional)</span></label>
                <Input
                  value={company.cedi_maps_link}
                  onChange={(e) => setCompany({ ...company, cedi_maps_link: e.target.value })}
                  className="rounded-sm"
                  placeholder="https://www.google.com/maps/place/.../@9.9281,-84.0907,..."
                  data-testid="cedi-maps-link-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Link largo de Google Maps. Si el link es corto (<code className="mono">maps.app.goo.gl</code>) no se pueden extraer las coordenadas — usá el botón de abajo en su lugar.
                </p>
              </div>
              <CediCoordsField
                lat={company.cedi_lat}
                lng={company.cedi_lng}
                onChange={(lat, lng) => setCompany({ ...company, cedi_lat: lat, cedi_lng: lng })}
                mapsLink={company.cedi_maps_link}
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving} className="btn-primary rounded-sm" data-testid="save-company-button">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Guardando..." : "Guardar Datos"}
            </Button>
          </div>
        </form>
      </Card>

      {/* WhatsApp upload webhook */}
      <WhatsAppWebhookCard />

      {/* Onboarding Express — plantilla todo-en-uno */}
      <Card
        className="border-0 rounded-sm p-6 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #002FA7 0%, #1E40AF 50%, #6366F1 100%)" }}
        data-testid="onboarding-express-card"
      >
        <div className="absolute -right-8 -top-8 opacity-10 pointer-events-none">
          <Rocket className="h-48 w-48" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="h-5 w-5" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Onboarding Express
            </h3>
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-white/20 backdrop-blur">
              60 segundos
            </span>
          </div>
          <p className="text-sm text-white/85 mb-4 max-w-3xl leading-relaxed">
            ¿Recién empezás? Bajá esta plantilla Excel única con 3 hojas (
            <strong>Colaboradores</strong>, <strong>Clientes</strong> e <strong>Instrucciones</strong>),
            llenala con tu equipo y tu cartera, y subila acá. Te creamos automáticamente:
          </p>
          <ul className="text-sm text-white/85 mb-5 space-y-1 max-w-3xl">
            <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 flex-shrink-0" /> Empleados con vacaciones, salarios y contactos de emergencia.</li>
            <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 flex-shrink-0" /> Cuentas de login para cada vendedor (con contraseña que te mostramos una sola vez).</li>
            <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 flex-shrink-0" /> Cartera de clientes asignada a su vendedor por código (V001, V002...) o por nombre.</li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={downloadOnboardingTemplate}
              className="rounded-sm bg-white text-[rgb(0,47,167)] hover:bg-white/90 font-semibold"
              data-testid="download-onboarding-template-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar plantilla
            </Button>
            <label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={importOnboarding}
                className="hidden"
                data-testid="import-onboarding-input"
              />
              <Button
                type="button"
                onClick={(e) => e.currentTarget.previousElementSibling.click()}
                variant="outline"
                className="rounded-sm border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                data-testid="import-onboarding-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                Subir plantilla completada
              </Button>
            </label>
          </div>
        </div>
      </Card>

      {/* Colaboradores */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[rgb(0,47,167)]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Recursos Humanos
            </h3>
            <span className="text-xs text-slate-500">{employees.length} colaboradores</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="download-employee-template-button">
                  <Download className="h-4 w-4 mr-2" />
                  Plantilla
                  <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem
                  onClick={() => downloadEmployeeTemplate("excel")}
                  className="rounded-sm cursor-pointer"
                  data-testid="download-employee-template-excel"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadEmployeeTemplate("csv")}
                  className="rounded-sm cursor-pointer"
                  data-testid="download-employee-template-csv"
                >
                  <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <label>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={importEmployees}
                className="hidden"
                data-testid="import-employees-input"
              />
              <Button
                type="button"
                onClick={(e) => e.currentTarget.previousElementSibling.click()}
                variant="outline"
                className="rounded-sm"
                data-testid="import-employees-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
            </label>
            <Button onClick={openNewEmployee} className="btn-primary rounded-sm" data-testid="add-employee-button">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Colaborador
            </Button>
          </div>
        </div>

        {employees.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No hay colaboradores registrados</p>
            <p className="text-xs text-slate-400 mt-1">Agrega los datos de tu equipo de trabajo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Nombre</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Cédula</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Puesto</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Teléfono</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Vendedor</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Fecha Ingreso</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id} className="table-row border-b border-slate-100 cursor-pointer" data-testid="employee-row" onClick={() => openEditEmployee(emp)}>
                    <td className="py-3 px-4 font-medium">{emp.nombre_completo}</td>
                    <td className="py-3 px-4 mono text-xs">{emp.cedula || "-"}</td>
                    <td className="py-3 px-4">{emp.puesto || "-"}</td>
                    <td className="py-3 px-4">{emp.telefono || "-"}</td>
                    <td className="py-3 px-4" data-testid={`employee-vendor-cell-${emp.id}`}>
                      {emp.es_vendedor ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <BadgeCheck className="h-3 w-3" />
                          {emp.codigo_vendedor || "vendedor"}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{emp.fecha_ingreso || "-"}</td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button onClick={() => openEditEmployee(emp)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0">
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button onClick={() => setDeleteEmpConfirm(emp)} variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0 text-red-600 hover:bg-red-50">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Mis Vendedores - resumen */}
      {(() => {
        const vendors = employees.filter((e) => e.es_vendedor);
        return (
          <Card className="bg-white border border-slate-200 rounded-sm p-6" data-testid="sellers-summary-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-emerald-700" />
                <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Mis Vendedores
                </h3>
                <span className="text-xs text-slate-500" data-testid="sellers-count">
                  {vendors.length} {vendors.length === 1 ? "vendedor" : "vendedores"}
                </span>
              </div>
            </div>
            {vendors.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-slate-200 rounded-sm" data-testid="sellers-empty-state">
                <UserCheck className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">Aún no marcaste a nadie como vendedor.</p>
                <p className="text-xs text-slate-400 mt-1">
                  Editá un colaborador y activá "Vendedor" para asignarle clientes y ver sus ventas en Analítica.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sellers-grid">
                {vendors.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => openEditEmployee(v)}
                    className="text-left flex items-center gap-3 p-3 border border-slate-200 rounded-sm hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                    data-testid={`seller-card-${v.id}`}
                  >
                    <div
                      className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: v.color_vendedor || "#10B981" }}
                    >
                      {(v.codigo_vendedor || v.nombre_completo || "?").slice(0, 3).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{v.nombre_completo}</div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                        <span className="mono font-medium text-[rgb(0,47,167)]">
                          {v.codigo_vendedor || "sin código"}
                        </span>
                        {v.telefono && <span className="truncate">{v.telefono}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        );
      })()}

      {/* Employee Dialog */}
      <Dialog open={empDialogOpen} onOpenChange={setEmpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmp ? "Editar Colaborador" : "Nuevo Colaborador"}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={submitEmployee}
            onKeyDown={(e) => {
              // Prevent accidental form submission when user presses Enter inside
              // vacation/emergency fields. Submit only via the Agregar/Actualizar button.
              if (e.key === "Enter" && e.target.tagName === "INPUT") {
                e.preventDefault();
              }
            }}
            className="space-y-4 mt-2"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1.5">Nombre Completo *</label>
                <Input
                  value={empForm.nombre_completo}
                  onChange={(e) => setEmpForm({ ...empForm, nombre_completo: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="employee-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Cédula</label>
                <Input
                  value={empForm.cedula}
                  onChange={(e) => setEmpForm({ ...empForm, cedula: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Puesto</label>
                <Input
                  value={empForm.puesto}
                  onChange={(e) => setEmpForm({ ...empForm, puesto: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Teléfono</label>
                <Input
                  value={empForm.telefono}
                  onChange={(e) => setEmpForm({ ...empForm, telefono: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Email</label>
                <Input
                  type="email"
                  value={empForm.email}
                  onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Fecha de Inicio en la Empresa</label>
                <Input
                  type="date"
                  value={empForm.fecha_ingreso}
                  onChange={(e) => setEmpForm({ ...empForm, fecha_ingreso: e.target.value })}
                  className="rounded-sm"
                  data-testid="employee-hiredate-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Salario</label>
                <Input
                  type="number"
                  step="0.01"
                  value={empForm.salario}
                  onChange={(e) => setEmpForm({ ...empForm, salario: e.target.value })}
                  className="rounded-sm"
                />
              </div>

              {/* Contacto de Emergencia */}
              <div className="col-span-2 pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4 text-[rgb(0,47,167)]" />
                  <h4 className="text-sm font-semibold text-[rgb(0,47,167)]">Contacto de Emergencia</h4>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Nombre del Contacto</label>
                <Input
                  value={empForm.contacto_emergencia_nombre}
                  onChange={(e) => setEmpForm({ ...empForm, contacto_emergencia_nombre: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ej: María Pérez"
                  data-testid="employee-emergency-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Teléfono del Contacto</label>
                <Input
                  value={empForm.contacto_emergencia_telefono}
                  onChange={(e) => setEmpForm({ ...empForm, contacto_emergencia_telefono: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ej: 8888-1234"
                  data-testid="employee-emergency-phone-input"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1.5">Notas</label>
                <Textarea
                  value={empForm.notas}
                  onChange={(e) => setEmpForm({ ...empForm, notas: e.target.value })}
                  className="rounded-sm"
                  rows={2}
                />
              </div>

              {/* Vendedor */}
              <div className="col-span-2 pt-3 border-t border-slate-200" data-testid="employee-vendor-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-emerald-700" />
                    <h4 className="text-sm font-semibold text-emerald-700">Vendedor</h4>
                    <span className="text-xs text-slate-500">
                      Marcá si esta persona es vendedor/a para asignarle clientes y verla en analítica.
                    </span>
                  </div>
                  <Switch
                    checked={!!empForm.es_vendedor}
                    onCheckedChange={(v) => setEmpForm({ ...empForm, es_vendedor: v })}
                    data-testid="employee-es-vendedor-switch"
                  />
                </div>
                {empForm.es_vendedor && (
                  <div className="grid grid-cols-3 gap-3 pl-1">
                    <div>
                      <label className="block text-xs font-medium mb-1.5">Código de vendedor *</label>
                      <Input
                        value={empForm.codigo_vendedor}
                        onChange={(e) => setEmpForm({ ...empForm, codigo_vendedor: e.target.value.toUpperCase().slice(0, 16) })}
                        className="rounded-sm mono"
                        placeholder="V001"
                        data-testid="employee-codigo-vendedor-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium mb-1.5">Color identificador</label>
                      <div className="flex items-center gap-2 flex-wrap" data-testid="employee-color-palette">
                        {VENDOR_COLOR_PALETTE.map((c) => (
                          <button
                            type="button"
                            key={c}
                            onClick={() => setEmpForm({ ...empForm, color_vendedor: c })}
                            className={`h-7 w-7 rounded-full border-2 transition-transform ${
                              empForm.color_vendedor === c ? "border-slate-900 scale-110" : "border-white hover:scale-105"
                            }`}
                            style={{ background: c, boxShadow: "0 0 0 1px #E2E8F0" }}
                            data-testid={`employee-color-${c.replace('#','')}`}
                            aria-label={`Color ${c}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Vacaciones */}
              <div className="col-span-2 pt-3 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-[rgb(0,47,167)]" />
                  <h4 className="text-sm font-semibold text-[rgb(0,47,167)]">Vacaciones Otorgadas</h4>
                  <span className="text-xs text-slate-500">
                    {empForm.vacaciones.length > 0 && `${empForm.vacaciones.reduce((s, v) => s + (parseFloat(v.dias) || 0), 0)} días totales`}
                  </span>
                </div>

                {empForm.vacaciones.length > 0 && (
                  <div className="border border-slate-200 rounded-sm overflow-hidden mb-3">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-2 px-2 font-medium text-slate-600">Tipo</th>
                          <th className="text-left py-2 px-2 font-medium text-slate-600">Desde</th>
                          <th className="text-left py-2 px-2 font-medium text-slate-600">Hasta</th>
                          <th className="text-right py-2 px-2 font-medium text-slate-600">Días</th>
                          <th className="text-left py-2 px-2 font-medium text-slate-600">Notas</th>
                          <th className="py-2 px-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {empForm.vacaciones.map(v => (
                          <tr key={v.id} className="border-t border-slate-100" data-testid="vacation-row">
                            <td className="py-2 px-2">{v.tipo}</td>
                            <td className="py-2 px-2 mono">{v.fecha_inicio}</td>
                            <td className="py-2 px-2 mono">{v.fecha_fin}</td>
                            <td className="py-2 px-2 text-right font-medium">{v.dias}</td>
                            <td className="py-2 px-2 text-slate-600 truncate max-w-[140px]">{v.notas || "-"}</td>
                            <td className="py-2 px-2 text-right">
                              <Button
                                type="button"
                                onClick={() => removeVacation(v.id)}
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                                data-testid={`remove-vacation-${v.id}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <label className="block text-xs font-medium mb-1 text-slate-500">Tipo</label>
                    <Select value={vacationForm.tipo} onValueChange={(v) => setVacationForm({ ...vacationForm, tipo: v })}>
                      <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="vacation-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Vacaciones">Vacaciones</SelectItem>
                        <SelectItem value="Permiso">Permiso</SelectItem>
                        <SelectItem value="Incapacidad">Incapacidad</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-medium mb-1 text-slate-500">Desde</label>
                    <Input
                      type="date"
                      value={vacationForm.fecha_inicio}
                      onChange={(e) => setVacationForm({ ...vacationForm, fecha_inicio: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVacation(); } }}
                      className="rounded-sm h-9 text-xs"
                      data-testid="vacation-start-input"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-medium mb-1 text-slate-500">Hasta</label>
                    <Input
                      type="date"
                      value={vacationForm.fecha_fin}
                      onChange={(e) => setVacationForm({ ...vacationForm, fecha_fin: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVacation(); } }}
                      className="rounded-sm h-9 text-xs"
                      data-testid="vacation-end-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-1 text-slate-500">Días</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={vacationForm.dias}
                      onChange={(e) => setVacationForm({ ...vacationForm, dias: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVacation(); } }}
                      className="rounded-sm h-9 text-xs"
                      placeholder="0"
                      data-testid="vacation-days-input"
                    />
                  </div>
                  <div className="col-span-1 flex items-end">
                    <Button
                      type="button"
                      onClick={addVacation}
                      className="btn-primary rounded-sm h-9 w-full p-0"
                      data-testid="add-vacation-button"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="col-span-12">
                    <Input
                      value={vacationForm.notas}
                      onChange={(e) => setVacationForm({ ...vacationForm, notas: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVacation(); } }}
                      className="rounded-sm h-9 text-xs"
                      placeholder="Notas (opcional)"
                      data-testid="vacation-notes-input"
                    />
                  </div>
                </div>
              </div>

              {/* Documentos del colaborador */}
              <div className="col-span-2 pt-3 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="h-4 w-4 text-[rgb(0,47,167)]" />
                  <h4 className="text-sm font-semibold text-[rgb(0,47,167)]">Carpeta de Documentos</h4>
                  <span className="text-xs text-slate-500">{empForm.documentos.length} archivo{empForm.documentos.length === 1 ? "" : "s"}</span>
                </div>

                {!editingEmp ? (
                  <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-sm border border-slate-200">
                    Guarda primero el colaborador para poder subir documentos.
                  </div>
                ) : (
                  <>
                    {empForm.documentos.length > 0 && (
                      <div className="border border-slate-200 rounded-sm overflow-hidden mb-3 divide-y divide-slate-100">
                        {empForm.documentos.map(d => (
                          <div key={d.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid="employee-doc-row">
                            <div className="flex items-center gap-2 min-w-0">
                              <Paperclip className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <div className="font-medium truncate">{d.titulo || d.original_filename}</div>
                                <div className="text-[10px] text-slate-500">
                                  {(d.size / 1024).toFixed(1)} KB · {d.uploaded_at?.split("T")[0]}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                type="button"
                                onClick={() => downloadEmployeeDoc(d.id)}
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                data-testid={`download-doc-${d.id}`}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                onClick={() => deleteEmployeeDoc(d.id)}
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                data-testid={`delete-doc-${d.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div>
                      <input
                        type="file"
                        id="employee-doc-upload"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadEmployeeDoc(f);
                          e.target.value = "";
                        }}
                        data-testid="employee-doc-input"
                      />
                      <label htmlFor="employee-doc-upload">
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-sm w-full cursor-pointer"
                          disabled={uploadingDoc}
                          onClick={() => document.getElementById("employee-doc-upload")?.click()}
                          data-testid="upload-employee-doc-button"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingDoc ? "Subiendo..." : "Subir documento (PDF, imágenes, Word, Excel — máx 10MB)"}
                        </Button>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEmpDialogOpen(false)} className="rounded-sm">Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm" data-testid="save-employee-button">
                {editingEmp ? "Actualizar" : "Agregar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteEmpConfirm} onOpenChange={(o) => !o && setDeleteEmpConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar colaborador?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar a {deleteEmpConfirm?.nombre_completo}? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteEmp} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de credenciales creadas (solo se ven una vez) */}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.isOnboarding ? (
                <Rocket className="h-5 w-5 text-[rgb(0,47,167)]" />
              ) : (
                <KeyRound className="h-5 w-5 text-emerald-700" />
              )}
              {importResult?.isOnboarding ? "Onboarding completado" : "Cuentas de vendedor creadas"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {importResult?.isOnboarding && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="onboarding-summary-grid">
                <div className="p-3 border border-slate-200 rounded-sm bg-slate-50 text-center">
                  <div className="text-2xl font-bold text-[rgb(0,47,167)]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    {importResult.employees_imported || 0}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">Colaboradores</div>
                </div>
                <div className="p-3 border border-slate-200 rounded-sm bg-slate-50 text-center">
                  <div className="text-2xl font-bold text-[rgb(0,47,167)]" style={{ fontFamily: "Cabinet Grotesk" }}>
                    {importResult.customers_imported || 0}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">Clientes</div>
                </div>
                <div className="p-3 border border-emerald-200 rounded-sm bg-emerald-50 text-center">
                  <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: "Cabinet Grotesk" }}>
                    {importResult.sellers_created?.length || 0}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-600 mt-0.5">Cuentas seller</div>
                </div>
              </div>
            )}

            {importResult?.sellers_created?.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  Guardá estas contraseñas <strong>ahora</strong>. Por seguridad solo se muestran una vez —
                  después solo podrás resetearlas desde el módulo "Vendedores".
                </p>
              </div>
            )}

            {importResult?.sellers_created?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-700">
                    {importResult.sellers_created.length} cuenta(s) lista(s) para usar
                  </h4>
                  <Button
                    type="button"
                    onClick={copyAllCredentials}
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    data-testid="copy-all-credentials-button"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1.5" />
                    Copiar todo
                  </Button>
                </div>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Nombre</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Contraseña</th>
                        <th className="py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.sellers_created.map((s) => (
                        <tr key={s.email} className="border-t border-slate-100" data-testid="created-seller-row">
                          <td className="py-2 px-3">{s.nombre_completo}</td>
                          <td className="py-2 px-3 mono text-xs">{s.email}</td>
                          <td className="py-2 px-3">
                            <span className="mono text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-900 border border-emerald-200">
                              {s.password}
                            </span>
                            {s.generated && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">auto</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              type="button"
                              onClick={() => copyCredentials(s)}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              data-testid={`copy-credential-${s.email}`}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importResult?.sellers_skipped?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">
                  {importResult.sellers_skipped.length} email(s) ya tenían cuenta — no se sobrescriben
                </h4>
                <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100 text-xs">
                  {importResult.sellers_skipped.map((email) => (
                    <li key={email} className="px-3 py-2 mono text-slate-600">{email}</li>
                  ))}
                </ul>
              </div>
            )}

            {importResult?.unresolved_vendors?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  {importResult.unresolved_vendors.length} vendedor(es) referenciado(s) sin match
                </h4>
                <p className="text-xs text-slate-500 mb-2">
                  Los clientes con estos códigos quedaron con el texto crudo. Revisá la columna{" "}
                  <code className="mono text-[11px]">vendedor_asignado</code> en tu Excel.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {importResult.unresolved_vendors.map((v) => (
                    <span
                      key={v}
                      className="mono text-[11px] px-2 py-0.5 rounded-sm bg-amber-50 text-amber-900 border border-amber-200"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {importResult?.errors?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  {importResult.errors.length} fila(s) con error
                </h4>
                <ul className="border border-red-200 rounded-sm divide-y divide-red-100 text-xs bg-red-50/40 max-h-40 overflow-y-auto">
                  {importResult.errors.map((err, i) => (
                    <li key={i} className="px-3 py-2 text-red-800">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                type="button"
                onClick={() => setImportResult(null)}
                className="btn-primary rounded-sm"
                data-testid="close-import-credentials-button"
              >
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendedores — administra las cuentas de vendedores del tenant */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-6" data-testid="company-sellers-section">
        <div className="flex items-center gap-2 mb-1">
          <UserCog className="h-5 w-5 text-[rgb(0,47,167)]" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Vendedores
          </h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Creá y administrá las cuentas de tus vendedores. Cada uno verá sólo "Tomar pedido" y "Mis pedidos".
        </p>
        <Sellers embedded />
      </Card>

      {/* Seguridad — 2FA + apariencia */}
      <SecurityCard />

      {/* Actividad — registro de toda la actividad de la empresa */}
      <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-6" data-testid="company-actividad-section">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-[rgb(0,47,167)]" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Actividad
          </h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Todo lo que se crea, edita o elimina en tu empresa queda registrado acá — útil para auditoría
          y para entender qué pasó en cualquier fecha.
        </p>
        <AuditLog embedded />
      </Card>
    </div>
  );
}

function WhatsAppWebhookCard() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const publicBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const uploadUrl = token ? `${publicBase}/api/wa/upload/${token}` : "";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/wa/token");
        if (alive) setToken(data.token);
      } catch {
        if (alive) setToken("");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      toast.success("URL copiada al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const doRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/wa/token/regenerate");
      setToken(data.token);
      toast.success("Token regenerado. La URL anterior dejó de funcionar.");
      setRegenOpen(false);
    } catch {
      toast.error("No se pudo regenerar el token");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-sm p-6" data-testid="wa-webhook-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="h-5 w-5 text-[#25D366]" />
        <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Webhook de WhatsApp
        </h3>
        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Avanzado</span>
      </div>
      <p className="text-sm text-slate-600 mb-4 max-w-3xl">
        Conectá un bot de WhatsApp Business (Twilio, Meta Cloud API, etc.) a esta URL. Cuando un vendedor envíe la foto de un pedido, el bot la reenvía aquí, la IA estructura el pedido y queda guardado automáticamente — sin tocar nada en tu computadora.
      </p>

      {loading ? (
        <div className="h-20 bg-slate-50 rounded-sm animate-pulse" />
      ) : (
        <div className="space-y-3">
          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              URL de subida (privada — no la compartas en público)
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={uploadUrl}
                readOnly
                className="rounded-sm font-mono text-xs bg-white"
                data-testid="wa-upload-url"
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copy} variant="outline" size="sm" className="rounded-sm shrink-0" data-testid="wa-copy-url">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar
              </Button>
            </div>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-700 hover:text-slate-900 font-medium select-none">
              Ejemplo con cURL
            </summary>
            <pre className="mt-2 p-3 rounded-sm bg-slate-900 text-emerald-200 text-[11px] overflow-x-auto font-mono leading-relaxed">
{`curl -X POST "${uploadUrl}" \\
  -F "file=@pedido.jpg" \\
  -F "nombre_cliente=Pulpería La Esquina" \\
  -F "nombre_vendedor=Ana Solís"`}
            </pre>
            <p className="text-xs text-slate-500 mt-2">
              <code>nombre_cliente</code> y <code>nombre_vendedor</code> son opcionales — si los omitís la IA los detecta de la imagen.
            </p>
          </details>

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRegenOpen(true)}
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="wa-regenerate-button"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Regenerar token
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Regenerar el token?</AlertDialogTitle>
            <AlertDialogDescription>
              La URL anterior dejará de funcionar inmediatamente. Tendrás que actualizar tu bot o integración con la nueva URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRegenerate}
              disabled={regenerating}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="wa-confirm-regenerate"
            >
              {regenerating ? "Regenerando…" : "Regenerar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}


/**
 * Seguridad — Two-factor authentication (TOTP) + dark-mode toggle.
 *
 * 2FA flow (matches backend in /app/backend/routers/auth.py):
 *   1. GET  /auth/2fa/status                → { enabled, has_secret }
 *   2. POST /auth/2fa/setup                 → { secret, qr_url }  (only if !has_secret)
 *   3. POST /auth/2fa/verify { code }       → enables it
 *   4. POST /auth/2fa/disable { password }  → turns it off
 *
 * The setup step is idempotent on the backend — calling it twice just
 * returns the existing secret/URL, so the UI is safe to re-fetch.
 */
import { useDarkMode as _useDarkMode } from "@/lib/darkMode";

function SecurityCard() {
  // 2FA state
  const [twoFA, setTwoFA] = useState({ enabled: false, has_secret: false, loading: true });
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);   // {secret, qr_url}
  const [setupCode, setSetupCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");

  // Dark mode
  const { theme, setTheme } = _useDarkMode();

  const reload2FA = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setTwoFA({ enabled: !!data.enabled, has_secret: !!data.has_secret, loading: false });
    } catch {
      setTwoFA(s => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { reload2FA(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setSetupCode("");
      setSetupOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error iniciando 2FA");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    if (setupCode.length < 6) {
      toast.error("Ingresá el código de 6 dígitos");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code: setupCode });
      toast.success("2FA activado");
      setSetupOpen(false);
      reload2FA();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || "Código inválido"));
      setSetupCode("");
    } finally {
      setBusy(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePwd) { toast.error("Ingresá tu contraseña"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success("2FA desactivado");
      setDisableOpen(false);
      setDisablePwd("");
      reload2FA();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al desactivar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-6" data-testid="company-security-section">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-[rgb(0,47,167)]" />
        <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Seguridad y apariencia
        </h3>
      </div>

      {/* 2FA block */}
      <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className={`h-4 w-4 ${twoFA.enabled ? "text-emerald-600" : "text-slate-400"}`} />
              <p className="font-semibold text-slate-900">Verificación de 2 pasos (2FA)</p>
              {twoFA.enabled && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="2fa-enabled-badge">
                  Activo
                </span>
              )}
            </div>
            <p className="text-xs text-slate-600">
              Protege tu cuenta pidiendo un código de tu app de autenticación (Google Authenticator, Authy, 1Password)
              cada vez que inicies sesión con contraseña.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {twoFA.loading ? (
              <span className="text-xs text-slate-400">Cargando…</span>
            ) : twoFA.enabled ? (
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setDisableOpen(true)}
                data-testid="2fa-disable-button"
              >
                Desactivar 2FA
              </Button>
            ) : (
              <Button
                size="sm"
                className="btn-primary rounded-sm"
                onClick={startSetup}
                disabled={busy}
                data-testid="2fa-enable-button"
              >
                <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                Activar 2FA
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Theme block */}
      <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 mt-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-1">
              {theme === "dark" ? <Moon className="h-4 w-4 text-slate-700" /> : <Sun className="h-4 w-4 text-amber-500" />}
              <p className="font-semibold text-slate-900">Apariencia</p>
            </div>
            <p className="text-xs text-slate-600">
              Elegí el tema que mejor te funcione. La preferencia se guarda en este dispositivo.
            </p>
          </div>
          <div className="flex items-center gap-1" data-testid="theme-toggle-group">
            {[
              { value: "light", label: "Claro", Icon: Sun },
              { value: "dark", label: "Oscuro", Icon: Moon },
              { value: "system", label: "Sistema", Icon: Smartphone },
            ].map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setTheme(value)}
                className={`h-8 px-2 inline-flex items-center gap-1 text-xs font-semibold rounded-sm border transition-colors ${
                  theme === value
                    ? "bg-[rgb(0,47,167)] text-white border-[rgb(0,47,167)]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}
                data-testid={`theme-toggle-${value}`}
                aria-pressed={theme === value}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Setup dialog — shows QR + secret + 6-digit confirmation */}
      <Dialog open={setupOpen} onOpenChange={(o) => !o && !busy && setSetupOpen(false)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Activar 2FA</DialogTitle>
          </DialogHeader>
          {setupData && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                1) Abrí tu app de autenticación y escaneá este QR.
              </p>
              <div className="flex justify-center bg-white p-3 border border-slate-200 rounded-sm">
                {/* Backend returns an otpauth:// URL. Use a public QR rendering API
                    so we don't need to bundle a QR library client-side. */}
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupData.qr_url || "")}`}
                  alt="QR de 2FA"
                  width={180}
                  height={180}
                  data-testid="2fa-qr-image"
                />
              </div>
              <details className="text-xs text-slate-600">
                <summary className="cursor-pointer text-[#002FA7] font-semibold">¿No podés escanear? Mostrá el código manual</summary>
                <code className="mt-1 block bg-slate-100 p-2 rounded-sm mono text-[11px] break-all">
                  {setupData.secret}
                </code>
              </details>
              <div>
                <p className="text-sm text-slate-700 mb-1.5">
                  2) Ingresá el código de 6 dígitos que muestra tu app:
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="text-center text-xl tracking-[0.4em] mono font-bold rounded-sm h-12"
                  data-testid="2fa-setup-code-input"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSetupOpen(false)} disabled={busy} className="rounded-sm">
                  Cancelar
                </Button>
                <Button onClick={confirmSetup} disabled={busy || setupCode.length < 6} className="btn-primary rounded-sm" data-testid="2fa-setup-confirm">
                  {busy ? "Validando…" : "Confirmar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable dialog — requires password */}
      <Dialog open={disableOpen} onOpenChange={(o) => !o && !busy && setDisableOpen(false)}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="2fa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Desactivar 2FA</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700 mb-2">
            Confirmá tu contraseña para desactivar la verificación de 2 pasos.
          </p>
          <Input
            type="password"
            value={disablePwd}
            onChange={(e) => setDisablePwd(e.target.value)}
            placeholder="Tu contraseña"
            className="rounded-sm"
            data-testid="2fa-disable-password"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setDisableOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={disable2FA} disabled={busy} className="bg-red-600 hover:bg-red-700 rounded-sm text-white" data-testid="2fa-disable-confirm">
              Desactivar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/**
 * CEDI coordinates capture field — used by the route optimizer.
 * Two paths:
 *  1) "Usar mi ubicación actual" — Geolocation API (zero typing).
 *  2) Try to extract from the pasted Google Maps link automatically.
 *
 * Why both? Because users often paste the short maps.app.goo.gl link
 * which does NOT contain coordinates, leaving the optimizer with no
 * starting point. The location button is the always-works fallback.
 */
function CediCoordsField({ lat, lng, onChange, mapsLink }) {
  const [capturing, setCapturing] = useState(false);
  const hasCoords = (lat && lng) || false;

  const capture = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización.");
      return;
    }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = +pos.coords.latitude.toFixed(6);
        const ln = +pos.coords.longitude.toFixed(6);
        onChange(la, ln);
        toast.success(`Ubicación capturada: ${la}, ${ln}`);
        setCapturing(false);
      },
      (err) => {
        setCapturing(false);
        if (err.code === 1) toast.error("Permiso denegado. Habilitá la ubicación en tu navegador.");
        else if (err.code === 2) toast.error("No se pudo obtener tu ubicación.");
        else toast.error("Tiempo agotado al obtener la ubicación.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const tryFromLink = () => {
    // Mirror the regex from route_planning.py to keep behaviour consistent.
    const patterns = [
      /@(-?\d+\.\d+),(-?\d+\.\d+)/,
      /[?&](?:q|query|ll|destination)=(-?\d+\.\d+),(-?\d+\.\d+)/,
      /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    ];
    for (const rx of patterns) {
      const m = (mapsLink || "").match(rx);
      if (m) {
        const la = parseFloat(m[1]);
        const ln = parseFloat(m[2]);
        if (Math.abs(la) + Math.abs(ln) > 0.0001) {
          onChange(+la.toFixed(6), +ln.toFixed(6));
          toast.success("Coordenadas extraídas del link de Maps.");
          return;
        }
      }
    }
    toast.error("Este link no contiene coordenadas. Usá el botón de ubicación.");
  };

  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
      <label className="block text-sm font-medium mb-1.5">
        Coordenadas del CEDI <span className="text-rose-600">*</span>
        <span className="block text-[11px] text-slate-500 font-normal mt-0.5">
          Necesario para que el botón "Optimizar" calcule rutas. Si no las tenés, capturá tu ubicación actual estando en el local.
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Button
          type="button"
          onClick={capture}
          disabled={capturing}
          className="btn-primary rounded-sm h-9"
          data-testid="cedi-capture-location-button"
        >
          <Crosshair className="h-4 w-4 mr-1.5" />
          {capturing ? "Capturando…" : "Usar mi ubicación actual"}
        </Button>
        {mapsLink && (
          <Button
            type="button"
            variant="outline"
            onClick={tryFromLink}
            className="rounded-sm h-9"
            data-testid="cedi-extract-from-link-button"
          >
            <MapPin className="h-4 w-4 mr-1.5" />
            Probar extraer del link
          </Button>
        )}
        {hasCoords && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200"
            data-testid="cedi-coords-badge"
          >
            ✓ {lat.toFixed(5)}, {lng.toFixed(5)}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          step="any"
          value={lat || ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0, lng || 0)}
          placeholder="Latitud"
          className="rounded-sm h-9 text-xs mono"
          data-testid="cedi-lat-input"
        />
        <Input
          type="number"
          step="any"
          value={lng || ""}
          onChange={(e) => onChange(lat || 0, parseFloat(e.target.value) || 0)}
          placeholder="Longitud"
          className="rounded-sm h-9 text-xs mono"
          data-testid="cedi-lng-input"
        />
      </div>
    </div>
  );
}
